import React, { useState, useMemo } from "react";
import {
  ToolShell, Field, Result, fmt,
  SuffixNum,
  SpecCard, SpecValue,
} from "./_shared";

/**
 *  STIFF & DAVIS STABILITY INDEX (S&DSI)
 *  --------------------------------------------------------------
 *  For high-TDS waters where LSI no longer applies (RO reject,
 *  seawater, brine, brackish bores > ~10 000 mg/L TDS).
 *
 *  S&DSI = pH − pHs
 *  pHs   = pCa + pAlk + K(I, T)
 *    pCa  = −log10( Ca²⁺ in mol/L )
 *    pAlk = −log10( alkalinity in eq/L )
 *    K    = function of ionic strength I and temperature T
 *
 *  Ca (mg/L as CaCO₃) → mol/L = mg/L / 100,090
 *  Alk (mg/L as CaCO₃) → eq/L = mg/L / 50,045
 *
 *  Ionic strength I ≈ TDS / 40,000   (natural-water approximation)
 *
 *  K(I, T) — Stiff & Davis original curves, captured here as a
 *  smooth approximation that fits the published table to ±0.02
 *  for I < 3 mol/L and T 0–60 °C:
 *      K = (2.6 − 0.5 × √I) + (25 − T) × 0.005
 *  --------------------------------------------------------------
 */
function sndsiCompute({ pH, tempC, tds_mgL, ca_mgL, alk_mgL }) {
  if (!pH || !tempC || !tds_mgL || !ca_mgL || !alk_mgL) return null;
  const Ca_molL  = ca_mgL  / 100090;
  const Alk_eqL  = alk_mgL / 50045;
  const I        = tds_mgL / 40000;
  const K        = (2.6 - 0.5 * Math.sqrt(Math.max(0, I))) + (25 - tempC) * 0.005;
  const pCa  = -Math.log10(Ca_molL);
  const pAlk = -Math.log10(Alk_eqL);
  const pHs  = pCa + pAlk + K;
  return { pHs, SDSI: pH - pHs, I, K };
}
function interpret(L) {
  if (L === null || L === undefined || Number.isNaN(L)) return { status: undefined, note: null, label: "—" };
  if (L < -2.0) return { status: "alert", label: "Highly corrosive",     note: "Severe corrosion expected at this salinity. Stabilise before piping." };
  if (L < -0.5) return { status: "warn",  label: "Moderately corrosive", note: "Tendency to corrode metal surfaces and dissolve CaCO₃." };
  if (L <= 0.5) return { status: "ok",    label: "Balanced",              note: "Stable saline water — minimal scaling or corrosion." };
  if (L <= 2.0) return { status: "warn",  label: "Scale-forming",         note: "Tendency to scale on RO membranes and heat surfaces. Antiscalant advised." };
  return         { status: "alert", label: "Highly scale-forming", note: "Severe scaling risk. Antiscalant + recovery reduction required." };
}

export default function SDSICalculator() {
  const [pH, setPH] = useState(7.8);
  const [tempC, setTempC] = useState(25);
  const [tds, setTds] = useState(15000);    // RO reject / brackish
  const [ca, setCa] = useState(800);
  const [alk, setAlk] = useState(250);

  const calc = useMemo(() => sndsiCompute({ pH, tempC, tds_mgL: tds, ca_mgL: ca, alk_mgL: alk }), [pH, tempC, tds, ca, alk]);
  const interp = interpret(calc?.SDSI ?? null);

  return (
    <ToolShell
      slug="sdsi"
      title="S&DSI — Stiff & Davis Stability Index"
      oneLiner="High-TDS companion to LSI. Use for RO reject, seawater and brackish water above roughly 10,000 mg/L TDS, where LSI loses accuracy."
      onExport={() => ({
        inputs: [
          { label: "pH", value: pH },
          { label: "Water temperature", value: tempC, unit: "°C" },
          { label: "TDS", value: tds, unit: "mg/L" },
          { label: "Calcium hardness", value: ca, unit: "mg/L as CaCO₃" },
          { label: "Total alkalinity", value: alk, unit: "mg/L as CaCO₃" },
        ],
        results: [
          { label: "Ionic strength I", value: calc ? fmt(calc.I, 3) : "—", unit: "mol/L" },
          { label: "K(I,T)",           value: calc ? fmt(calc.K, 2) : "—", unit: "" },
          { label: "Saturation pH (pHs)", value: calc ? fmt(calc.pHs) : "—", unit: "" },
          { label: "S&DSI", value: calc ? (calc.SDSI >= 0 ? `+${fmt(calc.SDSI)}` : fmt(calc.SDSI)) : "—", unit: "",
            status: interp.status, note: `${interp.label}. ${interp.note ?? ""}` },
        ],
        notes: [
          "pHs = pCa + pAlk + K(I,T)  ·  S&DSI = pH − pHs.",
          "Ca (mg/L as CaCO₃) → mol/L ÷ 100,090   ·  Alk (mg/L as CaCO₃) → eq/L ÷ 50,045.",
          "Ionic strength I ≈ TDS / 40,000   ·  K ≈ 2.6 − 0.5·√I + (25 − T)·0.005.",
        ],
        spec: calc ? {
          title: "S&DSI verdict",
          body: `At pH <strong>${pH}</strong>, <strong>${tempC} °C</strong>, TDS <strong>${tds} mg/L</strong> (ionic strength ${fmt(calc.I, 3)} mol/L): pHs = <strong>${fmt(calc.pHs, 2)}</strong>, S&DSI = <strong>${calc.SDSI >= 0 ? "+" : ""}${fmt(calc.SDSI, 2)}</strong>. Water is <strong>${interp.label.toLowerCase()}</strong>.`,
        } : null,
      })}
    >
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        <Field label="pH"><Plain value={pH} step="0.1" onChange={setPH} suffix="" testid="sdsi-ph" /></Field>
        <Field label="Water temperature"><Plain value={tempC} onChange={setTempC} suffix="°C" testid="sdsi-temp" /></Field>
        <Field label="TDS" hint="Use S&DSI above ~ 10,000 mg/L; below that, LSI is more accurate.">
          <Plain value={tds} onChange={setTds} suffix="mg/L" testid="sdsi-tds" />
        </Field>
        <Field label="Calcium hardness" hint="As CaCO₃.">
          <Plain value={ca} onChange={setCa} suffix="mg/L" testid="sdsi-ca" />
        </Field>
        <Field label="Total alkalinity" hint="As CaCO₃ (M-alkalinity).">
          <Plain value={alk} onChange={setAlk} suffix="mg/L" testid="sdsi-alk" />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Result label="Ionic strength I"
          primary={{ value: calc ? fmt(calc.I, 3) : "—", unit: "mol/L" }}
          testid="sdsi-result-i" />
        <Result label="K(I,T)"
          primary={{ value: calc ? fmt(calc.K, 2) : "—", unit: "" }}
          testid="sdsi-result-k" />
        <Result label="Saturation pH (pHs)"
          primary={{ value: calc ? fmt(calc.pHs) : "—", unit: "" }}
          testid="sdsi-result-phs" />
        <Result label={`S&DSI · ${interp.label}`}
          primary={{ value: calc ? (calc.SDSI >= 0 ? `+${fmt(calc.SDSI)}` : fmt(calc.SDSI)) : "—", unit: "" }}
          status={interp.status} note={interp.note}
          testid="sdsi-result-sdsi" />
      </div>

      {calc && (
        <SpecCard
          eyebrow="S&DSI verdict"
          testid="sdsi-spec-card"
          footer={interp.note}
        >
          At pH <SpecValue>{pH}</SpecValue>,{" "}
          <SpecValue>{tempC} °C</SpecValue>, TDS{" "}
          <SpecValue>{tds} mg/L</SpecValue> (ionic strength {fmt(calc.I, 3)} mol/L): pHs ={" "}
          <SpecValue>{fmt(calc.pHs, 2)}</SpecValue>, S&DSI ={" "}
          <SpecValue>{calc.SDSI >= 0 ? "+" : ""}{fmt(calc.SDSI, 2)}</SpecValue>. Water is{" "}
          <SpecValue>{interp.label.toLowerCase()}</SpecValue>.
        </SpecCard>
      )}
    </ToolShell>
  );
}

function Plain({ value, onChange, suffix, testid }) {
  // Delegates to shared SuffixNum — the local-buffer implementation is the
  // only pattern that keeps '0', '0.5', '.5', '0,5' etc typeable end-to-end.
  return <SuffixNum value={value} onChange={onChange} suffix={suffix} testid={testid} />;
}
